import { useCallback, useEffect, useState } from 'react';
import { useAudioDevice } from 'providers/AudioDeviceProvider';
import { classifyMicError } from 'utils/audio/classifyMicError';
import { stopStream } from 'utils/audio/stopStream';
import { trackError } from 'utils/trackError';

/** On the local workstation the provider owns the microphone, so this check stays inactive there. */
export default function useCheckMicAccess(enabled = true) {
  const { ready, mode } = useAudioDevice();
  const [micPermissionDenied, setMicPermissionDenied] = useState(false);
  const [micDeviceNotFound, setMicDeviceNotFound] = useState(false);

  const checkOrRequestMicPermission = useCallback(async () => {
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
      if (permissionStatus.state === 'granted') return;
      try {
        stopStream(await navigator.mediaDevices.getUserMedia({ audio: true }));
      } catch (error) {
        if (classifyMicError(error) === 'not-found') {
          setMicDeviceNotFound(true);
          return;
        }
        setMicPermissionDenied(true);
        if (!(error instanceof DOMException && error.name === 'NotAllowedError')) {
          trackError(error, { context: 'microphoneAccess', action: 'getUserMedia' });
        }
      }
    } catch (error) {
      console.error('Error checking/requesting microphone permission:', error);
      trackError(error, { context: 'microphoneAccess', action: 'permissionQuery' });
    }
  }, []);

  useEffect(() => {
    if (!enabled || !ready || mode !== 'passive') return;
    void checkOrRequestMicPermission();
  }, [enabled, ready, mode, checkOrRequestMicPermission]);

  const dismiss = useCallback(() => {
    setMicPermissionDenied(false);
    setMicDeviceNotFound(false);
  }, []);

  return { micPermissionDenied, micDeviceNotFound, dismiss };
}
